import { queryOptions } from '@tanstack/react-query';
import { PersonasService } from '@inference.engine/persona-api-client';

import { personasHttp } from '@/lib/api/personas-client';
import { downloadPdfBlob } from '@/shared/api/pdf-download.api';

import { PERSONA_QUERY_KEYS } from './query-keys';
import type { Persona } from '../model/types';

export const getAll = async (workspaceId: string): Promise<Persona[]> => {
  const response = await PersonasService.get({
    client: personasHttp,
    throwOnError: true,
    headers: { 'x-selected-workspace-id': workspaceId },
  });

  return Array.isArray(response.data) ? response.data : [];
};

export const getPersonasById = async (
  ids: string[],
  { workspaceId }: { workspaceId?: string } = {},
): Promise<Persona[]> => {
  if (!ids.length) {
    return [];
  }

  const response = await PersonasService.findMany({
    client: personasHttp,
    throwOnError: true,
    query: { ids: ids.join(',') },
    // Pass the workspace explicitly for the headless print page, which has no
    // ambient workspace context (same reason as `getSinglePersona`).
    ...(workspaceId ? { headers: { 'x-selected-workspace-id': workspaceId } } : {}),
  });

  return Array.isArray(response.data) ? response.data : [];
};

export const personasQuery = (workspaceId: string | null | undefined) =>
  queryOptions({
    queryKey: PERSONA_QUERY_KEYS.list(workspaceId!),
    queryFn: () => getAll(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const personasByIdsQuery = (workspaceId: string | null | undefined, ids: string[]) =>
  queryOptions({
    queryKey: PERSONA_QUERY_KEYS.byIds(workspaceId!, ids),
    queryFn: () => getPersonasById(ids, { workspaceId: workspaceId ?? undefined }),
    enabled: !!workspaceId && ids.length > 0,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

/**
 * Fetch a single persona by id, scoping to `workspaceId` via an explicit header
 * rather than the ambient store. Needed by callers with no workspace store — e.g.
 * the headless `/download/persona/:id` print page
 */
export const getSinglePersona = async (
  id: string,
  { workspaceId }: { workspaceId: string },
): Promise<Persona> => {
  const response = await PersonasService.findOne({
    client: personasHttp,
    path: { id },
    headers: { 'x-selected-workspace-id': workspaceId },
    throwOnError: true,
  });
  return response.data;
};

/**
 * Download a persona as a server-rendered PDF blob (see docs → PDF Export).
 * Hits `GET /audience/:id/pdf` on inference-api (persona export lives on the
 * workspace-scoped audience controller).
 */
export const downloadPersonaPdf = (
  id: string,
  { workspaceId }: { workspaceId: string },
  // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; every caller toasts its own translated message
): Promise<Blob> => downloadPdfBlob('audience', id, { label: 'Persona', params: { workspaceId } });
